import serverVars from 'server-vars';
import { fetchQuery, graphql, Environment } from 'react-relay';
import {
    recaptchaFeatureFlagQueryResponse,
    recaptchaFeatureFlagQuery,
} from './__generated__/recaptchaFeatureFlagQuery.graphql';
import widgetLoader from 'dibs-widget-loader';

import './styles/recaptcha.scss';

const NODE_RECAPTCHA_SITE_KEY_NAME = 'NODE_RECAPTCHA_SITE_KEY';

const sitekey = serverVars.get(NODE_RECAPTCHA_SITE_KEY_NAME);
const elId = `dibs-recaptcha`;

const query = graphql`
    query recaptchaFeatureFlagQuery($featureFlag: String!) {
        viewer {
            featureFlag(feature: $featureFlag)
        }
    }
`;

const isLoaded = (): boolean =>
    typeof window !== 'undefined' &&
    typeof window.grecaptcha !== 'undefined' &&
    typeof window.grecaptcha.render === 'function';

const isReady = new Promise<void>(resolve => {
    if (isLoaded()) {
        resolve();
    } else {
        const interval = setInterval(() => {
            if (isLoaded()) {
                clearInterval(interval);
                resolve();
            }
        }, 300);
    }
});

async function setUpCaptcha(): Promise<void> {
    if (document.getElementById(elId)) {
        return;
    }
    await widgetLoader.load('recaptcha');
    await isReady;

    // put callbacks on window or else functions get gc'd (i think?)
    window.dibsCaptchaCallbacks = { reject: () => {}, resolve: () => {} };

    const node = document.createElement('div');
    node.setAttribute('id', elId);
    node.setAttribute('data-sitekey', sitekey);
    document.body.appendChild(node);
    window.grecaptcha.render(node, {
        sitekey,
        size: 'invisible',
        callback: token => {
            window.dibsCaptchaCallbacks.resolve(token);
            window.grecaptcha.reset();
        },
        'error-callback': () => {
            window.dibsCaptchaCallbacks.reject();
            window.grecaptcha.reset();
        },
    });
}

/**
 * Executes recaptcha,
 * relayEnvironment only needed if featureFlag is provided
 */
async function executeCaptcha(args?: {
    featureFlag?: string;
    relayEnvironment?: Environment;
}): Promise<string | null> {
    const { featureFlag, relayEnvironment } = args || {};
    if (featureFlag && relayEnvironment) {
        const result: recaptchaFeatureFlagQueryResponse = await fetchQuery<
            recaptchaFeatureFlagQuery
        >(relayEnvironment, query, { featureFlag });
        if (!result?.viewer?.featureFlag) {
            return null;
        }
    }
    await setUpCaptcha();
    return await new Promise((resolve, reject) => {
        window.dibsCaptchaCallbacks.resolve = resolve;
        window.dibsCaptchaCallbacks.reject = reject;
        window.grecaptcha.execute();
    });
}

(async () => {
    await isReady;
    // for easy testing from console
    window.executeCaptcha = executeCaptcha;
})();

export { NODE_RECAPTCHA_SITE_KEY_NAME, executeCaptcha };
